/**
 * iter-135 — Landing page shown when the caller's band is suspended
 * by the platform administrator. We reach this route from the /api
 * client interceptor when the backend returns HTTP 402 with
 * ``code: band_suspended``. The reason is passed through
 * sessionStorage so the user sees the same detail the API sent.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ShieldAlert, LogOut, Mail } from "lucide-react";

export default function Suspended() {
  const [info, setInfo] = useState({});

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("vivo-suspend-reason");
      if (raw) setInfo(JSON.parse(raw));
    } catch {
      /* noop */
    }
  }, []);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("impersonation");
    sessionStorage.removeItem("vivo-suspend-reason");
    window.location.href = "/login";
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 bg-background"
      data-testid="suspended-page"
    >
      <div className="max-w-md w-full text-center space-y-6 bg-white/[0.02] border border-orange-500/30 rounded-xl p-8">
        <div className="mx-auto w-16 h-16 rounded-full bg-orange-500/10 flex items-center justify-center border border-orange-500/30">
          <ShieldAlert className="w-8 h-8 text-orange-400" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Band access suspended
          </h1>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            {info.detail ||
              "This band has been suspended by the platform administrator. Please contact support to restore access."}
          </p>
          {info.reason ? (
            <p
              className="mt-3 text-xs italic text-orange-300 break-words"
              data-testid="suspended-reason"
            >
              Reason: {info.reason}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col gap-2">
          <Button
            asChild
            className="w-full"
            data-testid="suspended-contact-support-btn"
          >
            <a href="mailto:support@vivomusicianmanagement.co.uk">
              <Mail className="w-4 h-4 mr-2" /> Contact support
            </a>
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={logout}
            data-testid="suspended-sign-out-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
          <Link
            to="/subscription"
            className="text-xs text-muted-foreground hover:text-foreground pt-1"
            data-testid="suspended-billing-link"
          >
            Manage subscription
          </Link>
        </div>
      </div>
    </div>
  );
}
